import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import MROSafetyReportForm from './forms/MROSafetyReportForm';

/**
 * ReporterApp - Dedicated application interface for safety reporters
 * 
 * This component provides:
 * - Streamlined interface focused solely on safety reporting
 * - No access to other MaXwell modules
 * - Mobile-optimized design for field use
 * - Direct PWA installation targeting
 * - Role-based authentication
 */

const ReporterApp = () => {
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState('guest'); // 'guest', 'login', 'authenticated'
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated (optional)
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        setAuthMode('authenticated');
      }
    };
    
    checkSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          setUser(session.user);
          setAuthMode('authenticated');
        } else {
          setUser(null);
          setAuthMode('guest');
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      setAuthMode('guest');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleReportSuccess = (reportData) => {
    console.log('Report submitted successfully:', reportData);
    // Show success message
  };

  const handleGuestReport = () => {
    // Allow guest reporting - form will handle anonymous submission
    return true;
  };

  // Show login screen if user chooses to authenticate
  if (authMode === 'login') {
    return <ReporterLogin 
      onSuccess={() => setAuthMode('authenticated')} 
      onCancel={() => setAuthMode('guest')} 
    />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal header for reporters */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 text-white px-3 py-1 rounded-lg text-sm font-medium">
                SMS Prime
              </div>
              <h1 className="text-xl font-semibold text-gray-900">Safety Reporting</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              {user ? (
                <>
                  <span className="text-sm text-gray-600">
                    Welcome, {user.email}
                  </span>
                  <button
                    onClick={handleSignOut}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <span className="text-sm text-gray-600">Guest Reporter</span>
                  <button
                    onClick={() => setAuthMode('login')}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Sign In
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* PWA Notice for guest users */}
      {!user && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-blue-800">
                  📱 <strong>Quick Access:</strong> You can report safety events as a guest or sign in for full features.
                </span>
              </div>
              <button
                onClick={() => setAuthMode('login')}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Sign In for Full Access
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content - the reporting form */}
      <main>
        <MROSafetyReportForm
          mode="create"
          onSubmitSuccess={handleReportSuccess}
          onCancel={() => window.history.back()}
          isGuestMode={!user}
        />
      </main>
    </div>
  );
};

// Login component specifically for reporters
const ReporterLogin = ({ onSuccess, onCancel }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      
      // Success - will be handled by auth state change
      if (onSuccess) onSuccess();
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold">SMS</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Safety Reporting Access
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to submit safety reports
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSignIn}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
          </div>

          {authError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-700">{authError}</p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            For technical support, contact your safety manager
          </p>
        </div>
      </div>
    </div>
  );
};

// Unauthorized access component
const UnauthorizedAccess = ({ userRole, onSignOut }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
    <div className="max-w-md w-full text-center">
      <div className="mx-auto h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
        <span className="text-red-600 text-xl">⚠️</span>
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h2>
      <p className="text-gray-600 mb-6">
        This area is restricted to authorized safety reporters only.
        {userRole && ` Your role: ${userRole}`}
      </p>
      <button
        onClick={onSignOut}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
      >
        Sign Out
      </button>
    </div>
  </div>
);

export default ReporterApp;
